import React, { useMemo, useState } from 'react';
import { Popover, Switch } from '@mui/material';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import CrownIcon from '@src/assets/icons/icon-crown.svg';
import Typography from '@src/new-components/Typography';
import Tooltip from '@src/components/Tooltip';
import DropdownCustom from '@src/new-components/Dropdown';
import { COLOR } from '@src/styles/color';
import SupportedLanguagesNote from './SupportedLanguagesNote';
import {
  StyledAIOptions,
  StyledRow,
  StyledRowLabelGroup,
  StyledRowLabelColumn,
  StyledRowRightGroup,
  StyledModelAnchor,
  StyledPopoverList,
  StyledPopoverItem,
  StyledPopoverItemText,
  StyledItalicTypography,
  StyledTipBox,
  StyledTipText,
} from './index.style';

const SWITCH_SX = {
  width: 40,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '2px',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: COLOR.indigo.default,
        opacity: 1,
      },
    },
    '&.Mui-disabled': {
      // Keep the thumb white (active color) when disabled — only the track
      // fades, so the switch reads as "disabled" without losing the
      // checked-state visual.
      color: '#fff!important',
      cursor: 'not-allowed',
      pointerEvents: 'auto',
      '& .MuiSwitch-thumb': {
        opacity: 1,
      },
      '& + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
  },
  '& .MuiSwitch-track': {
    borderRadius: 100,
    opacity: 1,
    backgroundColor: 'rgba(120, 120, 128, 0.16)',
  },
};

const getModelKey = (model) => model?.value || model?.model;
const getModelLabel = (model) =>
  model?.label || model?.model || model?.value || '';

/**
 * AI options atom — renders language + model + tip box with an optional toggle.
 *
 * @param {{ enabled?: boolean, language?: string, model?: object }} value — controlled state.
 * @param {(next) => void} onChange — called with next `value`.
 * @param {Array<{ value, label, labelCustom? }>} [languages] — language options; hides row when empty.
 *   `labelCustom` (ReactNode) overrides `label` in the dropdown list for custom rendering.
 * @param {Array<object>} [models] — model options (shape decided by consumer); hides row when empty.
 * @param {string[]} [supportedLanguages] — localized names of the languages the selected model
 *   accepts, rendered as a caption under the language label. Pass an empty array when the model
 *   has no whitelist — the caption then hides entirely.
 * @param {(model) => ReactNode} [renderModelDescription] — description line (14-regular label2).
 * @param {(model) => ReactNode} [renderModelCredit] — credit line (12-italic label2). Consumer
 *   usually formats via `t('creditPerSecond', { value })` or `t('creditPerSlide', { value })`.
 * @param {(model) => boolean} [isModelLocked] — paywalled model: shows a crown icon next to its
 *   name (list items and closed anchor). Selection stays allowed; the submit guard upsells.
 * @param {(model) => ReactNode} [renderModelLockedTooltip] — hover content for that crown.
 * @param {ReactNode} [children] — extra rows (voice picker, pronunciation, custom controls, ...)
 *   injected between the model row and the tip box. Rendered inside the body gate, so they hide
 *   together with language/model when the switch is off.
 * @param {boolean} [showSwitch=false] — shows the enable/disable switch row (PPTX flow).
 * @param {string} [aiSwitchLabel] — label next to the switch.
 * @param {string} [switchTooltip] — tooltip text for the info icon next to the switch.
 * @param {boolean} [switchDisabled=false] — disables the switch without showing the
 *   crown/paywall icon. Use when an external constraint (e.g. file format) forces
 *   the toggle to a fixed state. `locked` shows the crown but keeps the switch
 *   usable — the caller paywalls on submit.
 * @param {string} [languageLabel] — label for the language row.
 * @param {string} [modelLabel] — label for the model row.
 * @param {string} [languagePlaceholder] — placeholder when no language is selected.
 * @param {string} [searchPlaceholder] — placeholder for the searchable language dropdown.
 * @param {string} [tipText] — HTML tip text rendered at the bottom.
 */
const ProjectAIOptions = ({
  value,
  onChange,
  languages = [],
  supportedLanguages = [],
  models = [],
  renderModelDescription,
  renderModelCredit,
  isModelLocked,
  renderModelLockedTooltip,
  showSwitch = false,
  aiSwitchLabel,
  switchTooltip,
  locked = false,
  lockedTooltip,
  switchDisabled = false,
  // Header variant: 'switch' (default, PPTX/AUDIO) or 'dropdown' (video dubbing
  // subtitle-method selector, AIV-12773 US-03). When 'dropdown', `mode` /
  // `modeOptions` / `onModeChange` drive a select in place of the switch.
  headerVariant = 'switch',
  mode,
  modeOptions = [],
  onModeChange,
  // Content for the currently selected method, rendered directly under the
  // dropdown header (e.g. the .srt dropzone for the "upload existing subtitle"
  // method). Only shown for the dropdown header variant.
  modeContent,
  languageLabel,
  modelLabel,
  languagePlaceholder,
  searchPlaceholder,
  tipText,
  children,
}) => {
  const [modelAnchor, setModelAnchor] = useState(null);

  // Match legacy PPTX dynamic dropdown width — sized to the longest language
  // OR model label (160px floor). Keeps anchors aligned and fits long model
  // names like "vbee-transcribe-flash" without truncation.
  const dropdownWidth = useMemo(() => {
    const labelLengths = [
      ...languages.map((o) => (o.label || '').length),
      ...models.map((m) => getModelLabel(m).length),
      ...modeOptions.map((o) => (o.label || '').length),
    ];
    const maxLen = Math.max(...labelLengths, 0);
    return `${Math.max(160, Math.ceil(maxLen * 7.5) + 50)}px`;
  }, [languages, models, modeOptions]);

  const setField = (key, v) => onChange?.({ ...(value || {}), [key]: v });

  const isDropdownHeader = headerVariant === 'dropdown';
  const selectedModeLabel =
    modeOptions.find((o) => o.value === mode)?.label || '';

  // Body (model + language) is gated on `value.enabled` for both header
  // variants: the dropdown keeps `enabled` in sync (AUTO → true, others →
  // false), so only the AUTO method reveals the model/language rows.
  const shouldRenderBody =
    showSwitch || isDropdownHeader ? Boolean(value?.enabled) : true;

  const selectedLanguageLabel =
    languages.find((l) => l.value === value?.language)?.label ||
    languagePlaceholder;

  const selectedModelKey = getModelKey(value?.model);
  const selectedModelLabel = getModelLabel(value?.model);

  // Crown + upgrade tooltip for a paywalled model (mirrors the PPTX switch row).
  const renderModelCrown = (m) =>
    isModelLocked?.(m) ? (
      <Tooltip
        title={renderModelLockedTooltip?.(m) || ''}
        placement="top"
        arrow
      >
        <img
          src={CrownIcon}
          alt="crown"
          style={{ width: 20, height: 20, cursor: 'pointer', flexShrink: 0 }}
        />
      </Tooltip>
    ) : null;

  return (
    <StyledAIOptions>
      {isDropdownHeader && (
        <div className="container-mode">
          <StyledRow>
            <Typography variant="body-16-semibold" color={COLOR.darkGray}>
              {aiSwitchLabel}
            </Typography>
            <DropdownCustom
              variant="form-select-light"
              width={dropdownWidth}
              options={modeOptions}
              value={mode}
              onChange={(v) => onModeChange?.(v)}
              title={
                <Typography variant="body-14-regular" color={COLOR.darkGray}>
                  {selectedModeLabel}
                </Typography>
              }
            />
          </StyledRow>
          {modeContent && modeContent}
        </div>
      )}
      {showSwitch && (
        <StyledRow>
          <StyledRowLabelGroup>
            <Typography variant="body-16-semibold" color={COLOR.darkGray}>
              {aiSwitchLabel}
            </Typography>
            {switchTooltip && (
              <Tooltip title={switchTooltip} placement="top" arrow>
                <InfoOutlinedIcon
                  sx={{
                    width: 20,
                    height: 20,
                    color: COLOR.label2[64],
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            )}
          </StyledRowLabelGroup>
          <StyledRowRightGroup>
            {locked && (
              <Tooltip title={lockedTooltip || ''} placement="top" arrow>
                <img
                  src={CrownIcon}
                  alt="crown"
                  style={{ width: 20, height: 20, cursor: 'pointer' }}
                />
              </Tooltip>
            )}
            <Switch
              checked={Boolean(value?.enabled)}
              // `locked` only shows the crown: the paywall fires on submit, not on toggle
              disabled={switchDisabled}
              onChange={(e) => setField('enabled', e.target.checked)}
              sx={SWITCH_SX}
            />
          </StyledRowRightGroup>
        </StyledRow>
      )}

      {shouldRenderBody && (
        <>
          {models && models.length > 0 && (
            <StyledRow>
              <Typography variant="body-14-medium" color={COLOR.darkGray}>
                {modelLabel}
              </Typography>
              <div>
                <StyledModelAnchor
                  role="button"
                  tabIndex={0}
                  $width={dropdownWidth}
                  onClick={(e) => setModelAnchor(e.currentTarget)}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      variant="body-14-regular"
                      color={COLOR.darkGray}
                    >
                      {selectedModelLabel}
                    </Typography>
                    {renderModelCrown(value?.model)}
                  </div>
                  <ArrowDropDownRoundedIcon
                    sx={{
                      width: 24,
                      height: 24,
                      color: COLOR.navyBlue,
                      transition: 'transform 0.2s',
                      transform: modelAnchor ? 'rotate(180deg)' : 'none',
                    }}
                  />
                </StyledModelAnchor>
                <Popover
                  open={Boolean(modelAnchor)}
                  anchorEl={modelAnchor}
                  onClose={() => setModelAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      borderRadius: '8px',
                      width: '318px',
                      marginTop: '8px',
                      boxShadow:
                        '0px 4px 16px 0px rgba(0, 0, 0, 0.08) !important',
                    },
                  }}
                >
                  <StyledPopoverList>
                    {models.map((m) => {
                      const key = getModelKey(m);
                      const active = selectedModelKey === key;
                      const descriptionNode = renderModelDescription?.(m);
                      const creditNode = renderModelCredit?.(m);
                      return (
                        <StyledPopoverItem
                          key={key}
                          role="button"
                          tabIndex={0}
                          $active={active}
                          onClick={() => {
                            setField('model', m);
                            setModelAnchor(null);
                          }}
                        >
                          <StyledPopoverItemText>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <Typography
                                variant="body-14-medium"
                                color={COLOR.darkGray}
                              >
                                {getModelLabel(m)}
                              </Typography>
                              {renderModelCrown(m)}
                            </div>
                            {descriptionNode && (
                              <Typography
                                variant="body-12-regular"
                                color={COLOR.label2[64]}
                              >
                                {descriptionNode}
                              </Typography>
                            )}
                            {creditNode && (
                              <StyledItalicTypography
                                variant="body-12-regular"
                                color={COLOR.label2[64]}
                              >
                                {creditNode}
                              </StyledItalicTypography>
                            )}
                          </StyledPopoverItemText>
                          <CheckRoundedIcon
                            sx={{
                              width: 20,
                              height: 20,
                              color: COLOR.navyBlue,
                              opacity: active ? 1 : 0,
                            }}
                          />
                        </StyledPopoverItem>
                      );
                    })}
                  </StyledPopoverList>
                </Popover>
              </div>
            </StyledRow>
          )}
          {languages && languages.length > 0 && (
            <StyledRow>
              <StyledRowLabelColumn>
                <Typography variant="body-14-medium" color={COLOR.darkGray}>
                  {languageLabel}
                </Typography>
                <SupportedLanguagesNote labels={supportedLanguages} />
              </StyledRowLabelColumn>
              <DropdownCustom
                variant="form-select-light"
                width={dropdownWidth}
                options={languages}
                value={value?.language}
                onChange={(v) => setField('language', v)}
                searchable
                searchPlaceholder={searchPlaceholder}
                title={
                  <Typography variant="body-14-regular" color={COLOR.darkGray}>
                    {selectedLanguageLabel}
                  </Typography>
                }
              />
            </StyledRow>
          )}
          {tipText && (
            <StyledTipBox>
              <TipsAndUpdatesRoundedIcon
                sx={{
                  width: 20,
                  height: 20,
                  color: COLOR.blueV2,
                  flexShrink: 0,
                }}
              />
              <StyledTipText
                variant="body-14-regular"
                color={COLOR.darkGray}
                dangerouslySetInnerHTML={{ __html: tipText }}
              />
            </StyledTipBox>
          )}
        </>
      )}
      {children}
    </StyledAIOptions>
  );
};

export default ProjectAIOptions;
